import type { FC } from 'react';
import { Flex } from '@chakra-ui/react';
import { BackorderBanner } from '../../../../components/backorder-banner/BackorderBanner';
import { Text } from '../../../../ui-components';
import { useBackorderProductItemData } from '../../hooks/useBackorderProductData';
import { BaseItem } from './BaseItem';
import { CartItemPfaWarning } from './CartItemPfaWarning';
import { GiftRegistryLink } from './GiftRegistryLink';
import { SaveForLaterButton } from './SaveForLaterButton';
import { UpsellButton } from './UpsellButton';
import type { ProductItemEditor } from '../../../../bff-api/baskets/model';
import type { Product } from '../../../../bff-api/products/model';
import type { BaseItemProps } from './BaseItem';

export type GiftRegistryItemProps = {
    productItem: ProductItemEditor;
    product: Product;
    variation?: string;
    isExtendEligible?: boolean;
    hasEnoughStock?: boolean;
} & Omit<BaseItemProps, 'id'>;

export const GiftRegistryItem: FC<GiftRegistryItemProps> = (props) => {
    const { productItem, variation, isExtendEligible, hasEnoughStock } = props;

    const {
        isOnlyBackOrder,
        selectedQuantityInStock,
        selectedQuantityInBackorder,
        includesItemsInBackorder,
    } = useBackorderProductItemData(productItem.productId, productItem.quantity);

    return (
        <BaseItem
            {...props}
            id={`cart-item-gift-registry-${productItem.productId}`}
            middleSection={variation ? <Text>{variation}</Text> : null}
            footerSection={
                <Flex
                    id={`cart-item-gift-registry-${productItem.productId}-footer`}
                    width="100%"
                    flexDirection="column"
                    gap={6}
                    sx={{
                        ':empty': {
                            display: 'none',
                        },
                    }}
                >
                    <GiftRegistryLink productItem={productItem} />
                    <UpsellButton
                        productItem={productItem}
                        hasEnoughStock={hasEnoughStock}
                        isExtendEligible={isExtendEligible}
                    />
                    {includesItemsInBackorder && productItem.c_inStockDate ? (
                        <BackorderBanner
                            productId={productItem.productId}
                            c_inStockDate={productItem.c_inStockDate}
                            isOnlyBackOrder={isOnlyBackOrder}
                            selectedQuantityInStock={selectedQuantityInStock}
                            selectedQuantityInBackorder={selectedQuantityInBackorder}
                        />
                    ) : null}
                    <CartItemPfaWarning productItem={productItem} />
                </Flex>
            }
            buttonSection={<SaveForLaterButton productItem={productItem} />}
        />
    );
};

GiftRegistryItem.defaultProps = {
    variation: undefined,
    isExtendEligible: false,
    hasEnoughStock: true,
};
